/**
 * Issued cheques (BE-19) — the "Cheques" tab on Wallets.
 * Cheques the typing centre wrote when paying expenses. Clear / bounce / cancel while ISSUED.
 * Per bank: pending (ISSUED) cheques count and total.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Chip, Alert, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, CircularProgress, Link,
  IconButton, Tooltip, Card, CardContent, Stack, useTheme, useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as ClearIcon, Block as BounceIcon, Cancel as CancelIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { StatusChip, JournalEntryLink } from '../../components/accounting';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { expenseService } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';

const today = () => new Date().toISOString().slice(0, 10);

const IssuedCheques = ({ bankAccounts = [], canWrite = true, viewSwitch = null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filters, setFilters] = useState({ status: 'ISSUED', bankAccountId: '', from: '', to: '' });
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [dialog, setDialog] = useState(null); // { kind: 'clear'|'bounce'|'cancel', cheque }
  const [form, setForm] = useState({ date: today(), reason: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, issued] = await Promise.all([
        expenseService.getCheques(filters),
        expenseService.getCheques({ status: 'ISSUED' }),
      ]);
      setRows(list);
      setPending(issued);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load cheques'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // "Pending cheques: N (AED X)" per bank
  const perBank = useMemo(() => {
    const by = {};
    pending.forEach((c) => {
      const k = c.bankAccountId;
      by[k] = by[k] || { name: c.bankAccountName, count: 0, total: 0 };
      by[k].count += 1;
      by[k].total += Number(c.amount || 0);
    });
    return Object.entries(by);
  }, [pending]);

  const openDialog = (kind, cheque) => { setForm({ date: today(), reason: '' }); setDialog({ kind, cheque }); };

  const confirm = async () => {
    const { kind, cheque } = dialog;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'clear') await expenseService.clearCheque(cheque.id, form.date);
      if (kind === 'bounce') await expenseService.bounceCheque(cheque.id, form.date, form.reason.trim());
      if (kind === 'cancel') await expenseService.cancelCheque(cheque.id, form.reason.trim());
      setSnackbar({ clear: 'Cheque cleared', bounce: 'Cheque bounced — a paid-now expense is reversed, a bill becomes unpaid again', cancel: 'Cheque cancelled — a paid-now expense is reversed, a bill becomes unpaid again' }[kind]);
      setDialog(null);
      await load();
    } catch (err) {
      setError(serverMessage(err, 'The action failed'));
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const needsReason = dialog?.kind === 'bounce' || dialog?.kind === 'cancel';
  const actions = (c) => c.status === 'ISSUED' && canWrite && (
    <Box display="flex" gap={0.5} justifyContent="flex-end">
      <Tooltip title="Mark cleared"><IconButton size="small" color="success" onClick={() => openDialog('clear', c)}><ClearIcon /></IconButton></Tooltip>
      <Tooltip title="Bounced"><IconButton size="small" color="error" onClick={() => openDialog('bounce', c)}><BounceIcon /></IconButton></Tooltip>
      <Tooltip title="Cancel cheque"><IconButton size="small" onClick={() => openDialog('cancel', c)}><CancelIcon /></IconButton></Tooltip>
    </Box>
  );
  const statusChips = (c) => (
    <Box display="flex" gap={0.5} justifyContent="center">
      <StatusChip type="cheque" value={c.status} />
      {c.stale && <StatusChip tone="error" label="Stale" />}
    </Box>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {perBank.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {perBank.map(([id, b]) => (
            <Chip key={id} variant="outlined" color="warning" clickable
              label={`${b.name || 'Bank'} - pending cheques: ${b.count} (${formatCurrency(b.total)})`}
              onClick={() => setFilters((f) => ({ ...f, status: 'ISSUED', bankAccountId: id }))} />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        {viewSwitch}
        <TextField select size="small" label="Status" value={filters.status} onChange={set('status')} sx={{ minWidth: 140, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}>
          <MenuItem value="">All</MenuItem>
          {['ISSUED', 'CLEARED', 'BOUNCED', 'CANCELLED'].map((st) => <MenuItem key={st} value={st}><StatusChip type="cheque" value={st} /></MenuItem>)}
        </TextField>
        <TextField select size="small" label="Bank" value={filters.bankAccountId} onChange={set('bankAccountId')} sx={{ minWidth: 160, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }}>
          <MenuItem value="">All banks</MenuItem>
          {bankAccounts.filter((b) => b.accountType === 'OPERATIONS').map((b) => (
            <MenuItem key={b.id} value={b.id}>{b.bankName || b.accountName}</MenuItem>
          ))}
        </TextField>
        <TextField type="date" size="small" label="From" value={filters.from} onChange={set('from')} InputLabelProps={{ shrink: true }} sx={{ width: 150, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }} />
        <TextField type="date" size="small" label="To" value={filters.to} onChange={set('to')} InputLabelProps={{ shrink: true }} sx={{ width: 150, ...{ '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' } } }} />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>{rows.length} cheques</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">No cheques found</Typography></Paper>
          ) : rows.map((c) => (
            <Card key={c.id} variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>Cheque {c.chequeNumber}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>{c.payee} · {c.bankAccountName}</Typography>
                  </Box>
                  {statusChips(c)}
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{formatDate(c.chequeDate)}</Typography>
                    <Link component="button" underline="hover" variant="caption" onClick={() => navigate(`/accounting/expenses/${c.expenseId}`)}>{c.expenseNumber}</Link>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>{formatCurrency(c.amount)}</Typography>
                    <JournalEntryLink id={c.closingJournalEntryId} title="View clearing / bounce entry" />
                  </Box>
                </Box>
                {c.status === 'ISSUED' && canWrite && <Box mt={1}>{actions(c)}</Box>}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Cheque #</TableCell>
                <TableCell>Cheque Date</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Payee</TableCell>
                <TableCell>Expense</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell sx={{ width: 130 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No cheques found</Typography></TableCell></TableRow>
              ) : rows.map((c) => (
                <TableRow key={c.id} hover sx={{ '& td': { fontSize: '0.95rem' } }}>
                  <TableCell><Typography fontWeight={600} color="primary" fontSize="0.95rem">{c.chequeNumber}</Typography></TableCell>
                  <TableCell>{formatDate(c.chequeDate)}</TableCell>
                  <TableCell>{c.bankAccountName}</TableCell>
                  <TableCell>{c.payee}</TableCell>
                  <TableCell>
                    <Link component="button" underline="hover" fontSize="0.95rem" onClick={() => navigate(`/accounting/expenses/${c.expenseId}`)}>{c.expenseNumber}</Link>
                  </TableCell>
                  <TableCell align="right"><Typography fontWeight={600} fontSize="0.95rem">{formatCurrency(c.amount)}</Typography></TableCell>
                  <TableCell align="center">{statusChips(c)}</TableCell>
                  <TableCell align="right">
                    <Box display="inline-flex" alignItems="center" gap={0.5}>
                      {actions(c)}
                      <JournalEntryLink id={c.closingJournalEntryId} title="View clearing / bounce entry" />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {{ clear: 'Cheque cleared', bounce: 'Cheque bounced', cancel: 'Cancel cheque' }[dialog?.kind]} — {dialog?.cheque?.chequeNumber}
        </DialogTitle>
        <DialogContent>
          {dialog?.kind !== 'cancel' && (
            <TextField label="Date" type="date" size="small" fullWidth sx={{ mt: 1 }} value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
          )}
          {needsReason && (
            <TextField label="Reason" size="small" fullWidth sx={{ mt: 2 }} value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          )}
          {dialog?.kind === 'bounce' && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>The payment is undone: a paid-now expense is reversed, a bill becomes unpaid again.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Back</Button>
          <Button variant="contained" onClick={confirm}
            disabled={busy || (dialog?.kind !== 'cancel' && !form.date) || (needsReason && !form.reason.trim())}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default IssuedCheques;
